import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";

type UnavailableMode = "byok" | "hosted";

export function OnboardingModal({
  onChooseDemo,
}: {
  onChooseDemo: () => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const demoButton = useRef<HTMLButtonElement>(null);
  const [tooltip, setTooltip] = useState<UnavailableMode | null>(null);

  useEffect(() => {
    demoButton.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const showComingSoon = (mode: UnavailableMode): void => {
    setTooltip(mode);
  };

  return (
    <div className="onboarding-backdrop">
      <section
        className="onboarding-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby="onboarding-description"
      >
        <div className="onboarding-brand" aria-hidden="true">
          <span className="brand-mark">D</span>
          <span>Deskflow</span>
        </div>
        <small className="eyebrow">{t("onboarding.eyebrow")}</small>
        <h1 id="onboarding-title">{t("onboarding.title")}</h1>
        <p id="onboarding-description">{t("onboarding.description")}</p>

        <div className="api-mode-list">
          <button
            type="button"
            className="api-mode unavailable"
            aria-disabled="true"
            onClick={() => showComingSoon("byok")}
          >
            <span className="mode-icon">⌁</span>
            <span className="mode-copy">
              <strong>{t("onboarding.byok")}</strong>
              <small>{t("onboarding.byokDescription")}</small>
            </span>
            <span className="status-badge">{t("onboarding.comingSoon")}</span>
            {tooltip === "byok" && (
              <span className="mode-tooltip" role="status">
                {t("onboarding.tooltip")}
              </span>
            )}
          </button>

          <button
            type="button"
            className="api-mode unavailable"
            aria-disabled="true"
            onClick={() => showComingSoon("hosted")}
          >
            <span className="mode-icon">☁</span>
            <span className="mode-copy">
              <strong>{t("onboarding.hosted")}</strong>
              <small>{t("onboarding.hostedDescription")}</small>
            </span>
            <span className="status-badge">{t("onboarding.comingSoon")}</span>
            {tooltip === "hosted" && (
              <span className="mode-tooltip" role="status">
                {t("onboarding.tooltip")}
              </span>
            )}
          </button>

          <button
            ref={demoButton}
            type="button"
            className="api-mode available"
            onClick={onChooseDemo}
          >
            <span className="mode-icon">✦</span>
            <span className="mode-copy">
              <strong>{t("onboarding.demo")}</strong>
              <small>{t("onboarding.demoDescription")}</small>
            </span>
            <span className="mode-arrow" aria-hidden="true">
              →
            </span>
          </button>
        </div>
        <p className="onboarding-privacy">● {t("onboarding.privacy")}</p>
      </section>
    </div>
  );
}
